import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Plus, Loader2, CheckCircle2, AlertCircle, BookOpen,
  Image as ImageIcon, Share2, FileText, Search,
} from "lucide-react";
import TopNav from "../components/TopNav";
import api from "../lib/api";

/**
 * Books — grid view of every book the user has created. Each card is
 * a one-glance summary of the book's KDP submission readiness.
 *
 * Powered by the batched GET /api/books endpoint which already aggregates
 * counts (covers, listings, status) so the client doesn't have to make
 * N+1 calls.
 */

const Books = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [tab, setTab] = useState("all"); // "all" | "ready" | "draft"

  useEffect(() => {
    let mounted = true;
    api.get("/books").then((r) => {
      if (mounted) setData(r.data);
    }).catch(() => {
      if (mounted) setData({ books: [], counts: { total: 0, ready: 0, draft: 0 } });
    }).finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center gap-2 justify-center h-[40vh] text-sm text-[#5F5E5A]">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading your books…
        </div>
      </Layout>
    );
  }

  const books = data?.books || [];
  const counts = data?.counts || { total: 0, ready: 0, draft: 0 };

  // Filter + tab
  const q = filter.trim().toLowerCase();
  const visible = books.filter((b) => {
    if (tab === "ready" && !b.ready) return false;
    if (tab === "draft" && b.ready) return false;
    if (q) {
      const hay = (b.book_title + " " + b.author + " " + b.subtitle).toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  return (
    <Layout>
      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#5F5E5A] mb-1">All my books</p>
          <h1
            className="text-3xl tracking-tight text-[#2C2C2A]"
            style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}
          >
            Books library
          </h1>
          <p className="text-sm text-[#5F5E5A] mt-1" data-testid="books-counter-line">
            {counts.total} book{counts.total === 1 ? "" : "s"} · {counts.ready} ready · {counts.draft} draft
          </p>
        </div>
        <button
          onClick={() => navigate("/frontmatter")}
          data-testid="books-new-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] shadow-sm"
        >
          <Plus className="w-4 h-4" /> Start a new book
        </button>
      </div>

      {/* Tabs + filter */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div className="flex items-center gap-1 bg-[#F1EFE8] rounded-[8px] p-1" data-testid="books-tabs">
          {[
            { id: "all", label: `All (${counts.total})` },
            { id: "ready", label: `Ready (${counts.ready})` },
            { id: "draft", label: `Draft (${counts.draft})` },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`books-tab-${t.id}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                tab === t.id ? "bg-white text-[#534AB7] shadow-sm" : "text-[#5F5E5A] hover:text-[#2C2C2A]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5F5E5A]/60" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            data-testid="books-filter-input"
            placeholder="Filter by title or author"
            className="pl-9 pr-3 py-2 rounded-[8px] bg-white border border-[#D3D1C7] text-sm text-[#2C2C2A] focus:outline-none focus:border-[#534AB7] focus:ring-1 focus:ring-[#534AB7] w-72"
          />
        </div>
      </div>

      {/* Grid */}
      {books.length === 0 ? (
        <div className="bg-[#F1EFE8]/50 border border-dashed border-[#D3D1C7] rounded-[12px] p-16 text-center">
          <BookOpen className="w-9 h-9 text-[#534AB7] mx-auto" strokeWidth={1.4} />
          <h2 className="mt-4 text-xl text-[#2C2C2A]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
            No books yet
          </h2>
          <p className="mt-1 text-sm text-[#5F5E5A]">Start by creating a cover, generating a listing, or building front matter — they'll all appear here automatically.</p>
          <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
            <button
              onClick={() => navigate("/cover/new")}
              data-testid="books-empty-cta-cover"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489]"
            >
              <Plus className="w-4 h-4" /> Create a cover
            </button>
            <button
              onClick={() => navigate("/frontmatter")}
              data-testid="books-empty-cta"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[8px] border border-[#D3D1C7] text-[#2C2C2A] text-sm font-medium hover:bg-[#F1EFE8]"
            >
              <Plus className="w-4 h-4" /> Front matter
            </button>
          </div>
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-[#F1EFE8]/50 border border-dashed border-[#D3D1C7] rounded-[12px] p-10 text-center text-sm text-[#5F5E5A]">
          No books match this filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="books-grid">
          {visible.map((b, i) => (
            <BookCard key={b.fm_id || b.cover_id || `book-${i}`} book={b} />
          ))}
        </div>
      )}
    </Layout>
  );
};

const BookCard = ({ book: b }) => {
  // Cover-only books (no front-matter project yet) link straight to the
  // cover editor. Books with a front matter go to the unified overview.
  const target = b.fm_id ? `/book/${b.fm_id}` : (b.cover_id ? `/cover/${b.cover_id}` : "#");
  const testid = b.fm_id || b.cover_id || "untitled";
  return (
    <Link
      to={target}
      data-testid={`book-card-${testid}`}
      className="bg-white border border-[#D3D1C7] rounded-[12px] overflow-hidden hover:border-[#534AB7] transition-colors group"
    >
      <div className="aspect-[2/3] bg-[#F1EFE8] flex items-center justify-center overflow-hidden">
        {b.thumbnail_url ? (
          <img
            src={b.thumbnail_url}
            alt={`${b.book_title} cover`}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
          />
        ) : (
          <ImageIcon className="w-10 h-10 text-[#5F5E5A]/40" strokeWidth={1.2} />
        )}
      </div>
      <div className="p-4 space-y-2">
        <div>
          <h3
            className="text-base text-[#2C2C2A] line-clamp-1"
            style={{ fontFamily: "Outfit, Inter, sans-serif", fontWeight: 500 }}
          >
            {b.book_title || "Untitled cover"}
          </h3>
          {b.author && <p className="text-xs text-[#5F5E5A]">by {b.author}</p>}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {b.ready ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#E8F6F0] border border-[#1D9E75]/30 text-[#1D9E75] text-[10px] font-medium">
              <CheckCircle2 className="w-3 h-3" /> Ready for KDP
            </span>
          ) : !b.fm_id ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#FFF4E5] border border-[#E4D6BA] text-[#7A5C0E] text-[10px] font-medium">
              <AlertCircle className="w-3 h-3" /> Cover only · add front matter
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#FFF4E5] border border-[#E4D6BA] text-[#7A5C0E] text-[10px] font-medium">
              <AlertCircle className="w-3 h-3" /> {b.status === "final" ? "Need cover" : "Draft"}
            </span>
          )}
          {b.trim_size && <span className="text-[10px] text-[#5F5E5A]">{b.trim_size}</span>}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#5F5E5A] pt-1 border-t border-[#D3D1C7]">
          <span className="inline-flex items-center gap-1">
            <ImageIcon className="w-3 h-3" /> {b.covers} cover{b.covers === 1 ? "" : "s"}
          </span>
          <span className="inline-flex items-center gap-1">
            <FileText className="w-3 h-3" /> {b.listings} listing{b.listings === 1 ? "" : "s"}
          </span>
          {b.share_token && (
            <span className="inline-flex items-center gap-1 text-[#534AB7] ml-auto">
              <Share2 className="w-3 h-3" /> Shared
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

const Layout = ({ children }) => (
  <div className="min-h-screen bg-[#FAFAF9]">
    <TopNav />
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
      {children}
    </div>
  </div>
);

export default Books;
